import { useCallback, useMemo, useRef, useState } from 'react';
import { debounce, noop } from 'lodash';
import { useRenameAssetMutation } from '@frameio/graphql-client/generated';
import { useClearFocusedAsset } from '@frameio/selection';
import { useTrack } from '@frameio/shared/segment/hooks';
import { hasEsmProjectError } from '@frameio/shared/utils/hasEsmProjectError';
import { ToastAlert, useToast } from '@frameio/vapor';
import { AssetParent } from '../../shared/gql/generated/AssetParent';
import { RenameWarningDialog } from '../components';
import TEXT from './generated/TEXT/useRenameAsset';

export type RenameAssetArgs = {
  assetId: string;
  oldName: string;
  newName: string;
  onError?: () => void;
  onSuccess?: () => void;
  force?: boolean;
};

export type RenameAssetReturn = {
  extensionWarningDialog: React.ReactNode;
  // @note: Used for Finder 1 only, to fix V4BG-2813. Remove when deprecating.
  isRenaming: boolean;
  isRenameWarningShowing: boolean;
  renameAsset: (args: RenameAssetArgs) => void;
};

/**
 * The useRenameAsset hook can be used to rename an asset. It performs any
 * necessary cache updates. It also provides a warning dialog for when the user
 * changes the file extension. This warning does not apply to FolderAssets or
 * InteractiveLiveAssets (LiveUrl assets), since neither represents a file
 * with a meaningful extension.
 *
 * @param opts.onSuccess an optional callback fired if the rename succeeds
 * @returns function to call when ready to rename an asset
 */
export function useRenameAsset(): RenameAssetReturn {
  const [renameAsset, { client }] = useRenameAssetMutation();
  const { addToast } = useToast();
  const renameArgs = useRef<RenameAssetArgs | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const leastDestructiveRef = useRef(null);
  const clearFocusedAsset = useClearFocusedAsset();
  const track = useTrack();

  const { assetErrorMessage, folderErrorMessage } = TEXT;

  const renameAssetCallback = useCallback(
    /**
     * @param opts.__typename the asset's typename
     * @param opts.assetId the asset's id
     * @param opts.force don't check the extension and fire the rename mutation
     *                   immediately
     * @param opts.oldName the current name of the asset
     * @param opts.newName the desired new name of the asset
     * @param opts.onError an optional function to call if an error occurs
     * @param opts.projectId the id of the project the asset is in
     */
    (opts: RenameAssetArgs) => {
      const args = {
        force: false,
        onError: noop,
        onSuccess: noop,
        ...opts,
      };
      const { assetId, force, newName, oldName, onError, onSuccess } = args;

      const asset = client.cache.readFragment({
        fragment: AssetParent,
        fragmentName: 'AssetParent',
        id: client.cache.identify({
          __typename: 'Asset',
          id: assetId,
        }),
      });

      if (!asset || !asset.project || !asset.parent) return;

      const parentId = asset.parent.id;
      const projectId = asset.project.id;

      if (
        !force &&
        shouldWarnOnExtensionChange(asset.__typename, oldName, newName)
      ) {
        renameArgs.current = args;
        setShowWarning(true);
        return;
      }

      setIsRenaming(true);
      track('asset-renamed', { asset_id: asset.id }, { logToDatadog: true });
      renameAsset({
        variables: {
          input: {
            assetId: asset.id,
            name: newName,
          },
        },
        onCompleted() {
          // note: not refetching finder here because the event subscription is
          // handling that, and there's no way to prevent the refetch from the
          // event side at the moment
          setIsRenaming(false);
          clearFocusedAsset();
          onSuccess();
        },

        /**
         * @hack: this debounce is here to handle an edge case where renames are
         * being submitted twice from the inline editor. it only happens on error
         * and trying to move the debounce closer to those components has proved
         * unsuccesful.
         */
        onError: debounce((error) => {
          let errorMessage =
            asset?.__typename === 'FolderAsset'
              ? folderErrorMessage
              : assetErrorMessage;
          const graphQLErrors = error?.graphQLErrors?.[0] ?? [];
          const esmProjectErrorMessage = graphQLErrors
            ? hasEsmProjectError(
                client,
                projectId,
                graphQLErrors.extensions?.code,
                graphQLErrors.extensions?.value
              )
            : '';
          errorMessage = esmProjectErrorMessage ?? errorMessage;

          setIsRenaming(false);
          clearFocusedAsset();
          addToast(
            (closeToast, toastCloseLabel) => (
              <ToastAlert
                closeLabel={toastCloseLabel}
                onRequestClose={closeToast}
                title={errorMessage as string}
                variant="error"
              />
            ),
            TOAST_OPTIONS
          );

          onError();
        }, ERROR_DEBOUNCE),

        optimisticResponse: {
          __typename: 'Mutation',
          renameAsset: {
            __typename: 'RenameAssetPayload',
            asset: {
              __typename: asset.__typename ?? 'UnsupportedAsset',
              id: asset.id,
              name: newName,
              parent: {
                __typename: 'FolderAsset',
                id: parentId,
              },
              project: {
                __typename: 'Project',
                id: projectId,
              },
            },
          },
        },
      });
    },
    [
      addToast,
      assetErrorMessage,
      clearFocusedAsset,
      client,
      folderErrorMessage,
      renameAsset,
      track,
    ]
  );

  const extensionWarningDialog = useMemo(() => {
    const args = renameArgs.current;
    const oldExt = getExtension(args?.oldName ?? '');
    const newExt = getExtension(args?.newName ?? '');

    return (
      <RenameWarningDialog
        ref={leastDestructiveRef}
        isShowing={showWarning}
        newExt={newExt}
        oldExt={oldExt}
        onChangeClick={() => {
          if (!renameArgs.current) return;

          renameAssetCallback({
            ...renameArgs.current,
            force: true,
          });
          setShowWarning(false);
        }}
        onExited={() => {
          renameArgs.current = null;
        }}
        onKeepClick={() => {
          renameArgs.current?.onError?.();
          setShowWarning(false);
        }}
      />
    );
  }, [renameAssetCallback, showWarning]);

  return {
    extensionWarningDialog,
    isRenameWarningShowing: showWarning,
    isRenaming,
    renameAsset: renameAssetCallback,
  };
}

export function getExtension(name: string) {
  const parts = name.split('.');

  if (parts.length > 1) {
    return `.${parts.pop()}`;
  }

  return '';
}

/**
 * FolderAssets and InteractiveLiveAssets (live URLs) are not files with a
 * meaningful extension, so they never warn; other types warn on an extension change.
 */
export function shouldWarnOnExtensionChange(
  typename: string | undefined,
  oldName: string,
  newName: string
) {
  return (
    typename !== 'FolderAsset' &&
    typename !== 'InteractiveLiveAsset' &&
    getExtension(oldName) !== getExtension(newName)
  );
}

/**
 * Live URL assets (InteractiveLiveAsset) are named URLs rather than files, so
 * inline editing selects the whole name; other asset types select just the
 * base name, leaving the extension unselected.
 */
export function getAutoSelectForAsset(
  typename: string | undefined
): 'all' | 'name' {
  return typename === 'InteractiveLiveAsset' ? 'all' : 'name';
}

TEXT.DEFINE({
  assetErrorMessage: "Couldn't rename asset.",
  folderErrorMessage: "Couldn't rename folder.",
});

const TOAST_OPTIONS = { duration: 4000 };
const ERROR_DEBOUNCE = 200;
